// Shared SSN-fallback helpers — see refactor plans/ssn-fallback-jira-tickets.md.
// Pure module: no React, no Redux (per spec-doc — src/lib is framework-agnostic helpers).
import { RESUME_WORKFLOW_API_ERROR_CODES, SSN_CONFIG_TYPES } from 'utils/constants';

/**
 * Decide which SSN variant to request from the user, given the list of
 * remaining fallback types. Partial is preferred when both are available
 * (asks for the less-sensitive value first).
 *
 * @param {Array<string>} fallback - remaining fallback types (ssnConfig.remainingFallbacks)
 * @returns {string} SSN_CONFIG_TYPES.PARTIAL | SSN_CONFIG_TYPES.FULL | ''
 */
export const getFallbackType = (fallback) => {
    if (!fallback) return '';

    const hasFull = fallback.includes(SSN_CONFIG_TYPES.FULL);
    const hasPartial = fallback.includes(SSN_CONFIG_TYPES.PARTIAL);

    // first ask for partial ssn first
    if (hasFull && hasPartial) {
        return SSN_CONFIG_TYPES.PARTIAL;
    }

    // if only full, then ask for full ssn fallback
    if (hasFull) return SSN_CONFIG_TYPES.FULL;

    // if only partial, then ask for partial ssn fallback
    if (hasPartial) return SSN_CONFIG_TYPES.PARTIAL;
};

/**
 * True when the resume-workflow API returned an Auth DB error code that means
 * the user must supply additional information (SSN) before the workflow can
 * advance.
 *
 * @param {string} error - response.data.status.error from resumeWorkflowApi
 * @returns {boolean}
 */
export const isResumeWorkflowFallbackError = (error) =>
    RESUME_WORKFLOW_API_ERROR_CODES.includes(error);

/**
 * True when the user still has SSN fallback attempts left.
 *
 * @param {Array<string>} remainingFallbacks - ssnConfig.remainingFallbacks
 * @returns {boolean}
 */
export const hasFallbacksRemaining = (remainingFallbacks) => remainingFallbacks?.length > 0;

/**
 * Whether the SSN fields should be shown on the UserDetails form:
 *   1. SSN type explicitly configured (PARTIAL or FULL), or
 *   2. fallback verification is requested (askForFallback), or
 *   3. SSN not blocked and fallbacks are available.
 *
 * @param {object}  args
 * @param {object}  args.ssnConfig       - profile ssn_config ({ type, remainingFallbacks })
 * @param {*}       args.askForFallback  - fallback type requested (truthy) or false/null
 * @param {boolean} args.ssnBlocked      - SSN blocked for this customer
 * @returns {boolean}
 */
export const getSsnFieldVisibility = ({ ssnConfig, askForFallback, ssnBlocked }) => {
    const isPartialOrFullSSNConfigured =
        ssnConfig?.type === SSN_CONFIG_TYPES.PARTIAL || ssnConfig?.type === SSN_CONFIG_TYPES.FULL;

    const hasFallbacksAvailable =
        ssnConfig?.type === null && ssnConfig?.remainingFallbacks?.length > 0;

    return (
        isPartialOrFullSSNConfigured ||
        Boolean(askForFallback) ||
        (!ssnBlocked && Boolean(askForFallback) && hasFallbacksAvailable)
    );
};
